import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchSystemReports } from '../services/api';
import Diagnostics from './Diagnostics';
import '../styles/Diagnostics.css';

const MINIMUM_SYSTEMREPORTER_VERSION = '1.0.73';

const isValidVersion = (value: string): boolean => /^\d+(?:\.\d+)+$/.test(value);

const isAtLeastVersion = (current: string, minimum: string): boolean => {
    const currentParts = current.split('.').map(Number);
    const minimumParts = minimum.split('.').map(Number);
    const length = Math.max(currentParts.length, minimumParts.length);

    for (let index = 0; index < length; index += 1) {
        const currentPart = currentParts[index] ?? 0;
        const minimumPart = minimumParts[index] ?? 0;
        if (currentPart !== minimumPart) return currentPart > minimumPart;
    }

    return true;
};

const DiagnosticsPage: React.FC = () => {
    const { serialNumber = '' } = useParams<{ serialNumber: string }>();
    const [systemReporterVersion, setSystemReporterVersion] = useState('');
    const [loadingVersion, setLoadingVersion] = useState(true);
    const [versionError, setVersionError] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setLoadingVersion(true);
        setVersionError('');

        void fetchSystemReports(serialNumber, 1)
            .then((reports) => {
                if (controller.signal.aborted) return;
                const version = reports[0]?.systemReporterVersion;
                setSystemReporterVersion(typeof version === 'string' ? version.trim().replace(/^v/i, '').split('+')[0] : '');
            })
            .catch((error: unknown) => {
                if (!controller.signal.aborted) {
                    setVersionError(error instanceof Error ? error.message : String(error));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoadingVersion(false);
            });

        return () => controller.abort();
    }, [serialNumber]);

    const canRetrieveLogs = isValidVersion(systemReporterVersion)
        && isAtLeastVersion(systemReporterVersion, MINIMUM_SYSTEMREPORTER_VERSION);

    return (
        <main className="diagnostics-page">
            <div className="diagnostics-page-header">
                <div>
                    <p className="diagnostics-page-eyebrow">Device diagnostics</p>
                    <h1>Diagnostics / logs</h1>
                    <p>Serial number: <strong>{serialNumber}</strong></p>
                </div>
                <Link className="secondary-button diagnostics-back-link" to={`/devices?serialNumber=${encodeURIComponent(serialNumber)}`}>
                    Back to device
                </Link>
            </div>
            {loadingVersion && <p role="status">Checking SystemReporter version...</p>}
            {!loadingVersion && versionError && (
                <p className="diagnostics-error" role="alert">
                    Unable to check the installed version: {versionError}. Log retrieval remains disabled.
                </p>
            )}
            {!loadingVersion && <Diagnostics
                key={serialNumber}
                serialNumber={serialNumber}
                systemReporterVersion={systemReporterVersion}
                canRetrieveLogs={canRetrieveLogs}
            />}
        </main>
    );
};

export default DiagnosticsPage;
